import {decryptKBItem, getKBKey, setKBKey} from "../../App/cryptoUtils.js";
import {deleteAPICache, setAPICache} from "../Cache.js";
import {APIRequest} from "../APIRequest.js";
import {uploadFileAPI} from "./Files.js";
import {deleteRecentApp, kbUserData, RAW_FILES_URL} from "../../utils";
import {GAEvent} from "../../App/GA";

export const KB_API_URL = 'https://kb.openkbs.com/';

const timeout = 20000;
export const decryptKBFields = (kb, kbId) => {
    const data = {...kb};

    if (kb.kbTitle) data.kbTitle = decryptKBItem(kb.kbTitle, kbId);
    if (kb.kbDescription) data.kbDescription = decryptKBItem(kb.kbDescription, kbId);
    if (kb.kbInstructions) data.kbInstructions = decryptKBItem(kb.kbInstructions, kbId);
    if (kb.OpenAIAPIKey) data.OpenAIAPIKey = decryptKBItem(kb.OpenAIAPIKey, kbId);

    return data;
}

const ongoingRequests = {};

export const getKB = async (decryptFields = true) => {
    const token = localStorage.getItem('kbJWT');
    const kbId = kbUserData()?.kbId

    if (!kbId) return;

    const cacheKey = `getKB-${kbId}`;

    // Check if there's an ongoing request for the same kbId and action
    if (!ongoingRequests[cacheKey]) {
        // If not, create a new request and store the promise
        ongoingRequests[cacheKey] = APIRequest('post', KB_API_URL, {
            token,
            action: 'getKB'
        }, {
            headers: {
                'Content-Type': 'application/json',
            },
        }).finally(() => {
            // Once the request is finished, remove it from the cache
            delete ongoingRequests[cacheKey];
        });
    }

    // Wait for the request to resolve and get the response
    const response = await ongoingRequests[cacheKey];
    const kb = response?.data;

    if (!kb) return {};

    if (!getKBKey(kbId)) setKBKey(kbId, kb.key);

    let data = {...kb};

    if (decryptFields) data = decryptKBFields(data, kbId)

    return data;
};

export const deleteKB = async (kbId) => {
    const token = localStorage.getItem("clientJWT");
    try {
        const response = await APIRequest('post', KB_API_URL, {
            token,
            action: 'delete',
            kbId,
        });

        deleteRecentApp(kbId)
        deleteAPICache('listKBs');

        return response?.data;
    } catch (error) {
        if (error?.response?.data?.hasItems) {
            return {hasItems: true};
        }
        console.error('API request error:', error);
        throw error;
    }
};

export const deleteAPIKey = async (apiKey) => {
    const token = localStorage.getItem('kbJWT');
    try {
        const response = await APIRequest('post', KB_API_URL, {
            token,
            action: 'deleteAPIKey',
            apiKey
        });
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const getAPIKeys = async () => {
    const token = localStorage.getItem('kbJWT');
    try {
        const response = await APIRequest('post', KB_API_URL, {
            token,
            action: 'getAPIKeys'
        });
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};


export const createAPIKey = async (APIKeyName, APIKeyPermissions = { resources: '*' }) => {
    const token = localStorage.getItem('kbJWT');

    const AESKey = getKBKey(kbUserData()?.kbId);

    try {
        const response = await APIRequest('post', KB_API_URL, {
                token,
                action: 'createAPIKey',
                APIKeyName,
                APIKeyPermissions,
                AESKey
        });
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
}

// kbid fffbc598-2157-4759-a844-eca258be90b0
// 5ad1234b60947cc7170c85f3af50402a00b2331c996832ea3527b6c226212d6b pace256
// a6e7a7dda611d4bda41b26bdab58934899e162b44caf998fe559b211d4cbdca5 proofof
export const shareKBWith = async (targetUserId) => {
    const token = localStorage.getItem('kbJWT');
    try {
        const response = await APIRequest('post', KB_API_URL, {token, action: 'shareKBWith', targetUserId},
            {timeout});
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const unshareKBWithMe = async (kbId) => {
    const token = localStorage.getItem("clientJWT");
    try {
        const response = await APIRequest('post', KB_API_URL, {token, action: 'unshareKBWithMe', kbId}, {timeout});
        deleteAPICache('getKBsSharedWithMe');
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const unshareKBWith = async (targetUserId) => {
    const token = localStorage.getItem('kbJWT');
    try {
        const response = await APIRequest('post', KB_API_URL, {token, action: 'unshareKBWith', targetUserId}, {timeout});
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const getEmail = async () => {
    const token = localStorage.getItem('kbJWT');
    try {
        const response = await APIRequest('post', KB_API_URL, {token, action: 'getEmail'}, {timeout});
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const getKBsSharedWithMe = async () => {
    const token = localStorage.getItem("clientJWT");
    try {
        const response = await APIRequest('post', KB_API_URL, {token, action: 'getKBsSharedWithMe'}, {timeout});
        setAPICache(response?.data, 'getKBsSharedWithMe');
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const getItemTypes = async () => {
    const token = localStorage.getItem('kbJWT');
    try {
        const response = await APIRequest('post', KB_API_URL, {
            token,
            action: 'getItemTypes',
        }, {timeout});
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const deleteItemType = async (itemType) => {
    const token = localStorage.getItem('kbJWT');
    try {
        const response = await APIRequest('post', KB_API_URL, {
            token,
            action: 'deleteItemType',
            itemType
        }, {timeout});
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

const checkAttrs = (attributes) => {
    let seen = {};
    if (attributes?.length) {
        attributes.forEach(attr => {
            if (seen[attr.attrType]) {
                alert('Invalid Attributes ' + attr.attrType + ' attrType seen twice')
                return true;
            }
            if (attr.attrType) seen[attr.attrType] = 1;
        })
    }
}

export const updateItemType = async (itemType, attributes, embeddingTemplate) => {
    const token = localStorage.getItem('kbJWT');

    if (checkAttrs(attributes)) return;

    try {
        const response = await APIRequest('post', KB_API_URL, {
            token,
            action: 'updateItemType',
            itemType,
            embeddingTemplate,
            attributes,
        }, {timeout});
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const createItemType = async (itemType, attributes, embeddingTemplate) => {
    const token = localStorage.getItem('kbJWT');

    if (checkAttrs(attributes)) return;

    try {
        const response = await APIRequest('post', KB_API_URL, {
            token,
            action: 'createItemType',
            itemType,
            embeddingTemplate,
            attributes
        }, {timeout});
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const getKBShares = async () => {
    const token = localStorage.getItem('kbJWT');
    try {
        const response = await APIRequest('post', KB_API_URL, {token, action: 'getKBShares'}, {timeout});
        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const saveKB = async ({action, kbTitle, pwaName, kbDescription, kbInstructions, chatVendor, chatEndpoint, model, OpenAIAPIKey,
                             voiceId, videoVendor, videoVoiceId, key, fileData, inputTools, frontendTokenLimit,
                             accountId, walletPublicKey, walletPrivateKey, itemTypes, embeddingDimension,
                             embeddingModel, searchEngine, installedAppId}) => {

    const token = action === 'create'
        ? localStorage.getItem("clientJWT")
        : localStorage.getItem('kbJWT');

    if (action === 'create') GAEvent("create_kb", { value: 5 })
    if (action === 'update') GAEvent("update_kb", { value: 1 })

    try {
        const params = {
            token,
            action,
            kbTitle,
            pwaName,
            kbDescription,
            kbInstructions,
            chatVendor,
            chatEndpoint,
            model,
            OpenAIAPIKey,
            voiceId,
            videoVendor,
            videoVoiceId,
            key,
            fileData,
            inputTools,
            frontendTokenLimit,
            accountId, walletPublicKey, walletPrivateKey,
            itemTypes, embeddingDimension,
            embeddingModel, searchEngine, installedAppId
        };

        const response = await APIRequest('post', KB_API_URL, params, {timeout});

        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const listKBs = async () => {
    const token = localStorage.getItem("clientJWT");

    const response = await APIRequest('get', KB_API_URL, {
        params: {
            action: 'list',
            token: token
        }
    });

    setAPICache(response?.data, 'listKBs');

    return response.data;
};

export const uploadJsonFileAPI = async (jsonString, fileName, onUploadProgress) => {
    // Ensure the JSON string is valid
    JSON.parse(jsonString);
    const mimeType = 'application/json';

    // Create a blob from the JSON string with the specified mimeType
    const blob = new Blob([jsonString], {type: mimeType});

    // Convert the blob to a file
    const file = new File([blob], fileName, {type: mimeType});

    return uploadFileAPI(file, 'files', onUploadProgress);
};

export const updateChatModel = async (model, chatVendor) => {
    const token = localStorage.getItem('kbJWT');

    try {
        const response = await APIRequest('post', KB_API_URL, {
            token,
            action: 'updateChatModel',
            model, chatVendor
        });

        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
}

export const updateSearchSettings = async (embeddingModel, embeddingDimension, searchEngine) => {
    const token = localStorage.getItem('kbJWT');

    try {
        const response = await APIRequest('post', KB_API_URL, {
            token,
            action: 'updateSearchSettings',
            embeddingModel,
            searchEngine,
            embeddingDimension: embeddingModel === 'none' ? 'none' : embeddingDimension
        }, { timeout });

        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
}

export const getSharedChat = async (kbId, shareId) => {
    const response = await APIRequest('get',`${RAW_FILES_URL}/files/${encodeURIComponent(kbId)}/${encodeURIComponent(shareId)}.json`);
    return response;

}

export const searchItems = async (text, kbId, k = 100) => {
    const token = localStorage.getItem('kbJWT');

    let q = text;

    try {
        const response = await APIRequest('get','https://search.openkbs.com', {
            params: { token, kbId, q, k }
        });

        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const createPushSubscription = async (pushSubscription, doNotDisturbStart = null, doNotDisturbEnd = null) => {
    const token = localStorage.getItem('kbJWT');
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
        const response = await APIRequest('post', KB_API_URL, {
            token,
            action: 'createPushSubscription',
            pushSubscription: JSON.stringify(pushSubscription),
            timezone, // Send timezone information to the backend
            doNotDisturbStart,
            doNotDisturbEnd
        }, {timeout});

        return response;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};